import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FlaskConical } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { useSiteSettings, whatsappLink } from "@/lib/SiteSettingsContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSiteSettings();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        return api.get(`/categories/${data.category_slug}`);
      })
      .then(({ data }) => setCategory(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="py-32 text-center text-slate-500" data-testid="product-loading">Yükleniyor...</div>;
  }

  if (notFound || !product) {
    return (
      <div className="py-32 text-center" data-testid="product-not-found">
        <h1 className="heading-font text-3xl text-[#0A2540] mb-4">Ürün bulunamadı</h1>
        <Link to="/urunler" className="text-[#0066FF] hover:underline">
          Tüm ürünleri görüntüle
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page">
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <Link
            to="/urunler"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0066FF] mb-8"
            data-testid="back-to-products"
          >
            <ArrowLeft size={16} /> Tüm ürünler
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <div className="aspect-square bg-slate-100 overflow-hidden">
                <img
                  src={resolveImage(product.image_url)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              </div>
            </div>
            <div className="lg:col-span-6">
              {category && (
                <Link
                  to={`/urunler?kategori=${category.slug}`}
                  className="inline-block text-xs uppercase tracking-[0.25em] text-[#0066FF] font-semibold mb-4 hover:underline"
                  data-testid="product-category-link"
                >
                  {category.name}
                </Link>
              )}
              <h1 className="heading-font text-4xl md:text-5xl text-[#0A2540] leading-tight" data-testid="product-name">
                {product.name}
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed" data-testid="product-short-description">
                {product.short_description}
              </p>

              {product.description && (
                <div className="mt-8 prose prose-slate text-slate-700 leading-relaxed" data-testid="product-description">
                  <p>{product.description}</p>
                </div>
              )}

              {product.specs && product.specs.length > 0 && (
                <div className="mt-10 border border-slate-200">
                  <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
                    <FlaskConical size={16} className="text-[#0066FF]" />
                    <h3 className="heading-font text-sm uppercase tracking-wider text-[#0A2540]">
                      Teknik Özellikler
                    </h3>
                  </div>
                  <table className="w-full text-sm" data-testid="product-specs-table">
                    <tbody>
                      {product.specs.map((s, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-6 py-3 text-slate-600 w-1/2 border-b border-slate-100">
                            {s.key}
                          </td>
                          <td className="px-6 py-3 text-[#0A2540] font-medium border-b border-slate-100">
                            {s.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-10 p-6 bg-[#0A2540] text-white">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck size={18} className="text-[#0066FF]" />
                  <h3 className="heading-font text-lg">Detaylı bilgi için iletişime geçin</h3>
                </div>
                <p className="text-sm text-white/70 mb-5">
                  Ürün hakkında teknik detaylar, kullanım alanları ve özel
                  formülasyon talepleriniz için bize WhatsApp üzerinden
                  ulaşabilirsiniz.
                </p>
                <a
                  href={whatsappLink(
                    settings.whatsapp,
                    `Merhaba, ${product.name} ürünü hakkında bilgi almak istiyorum`
                  )}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ea855] text-white px-6 py-3 text-sm font-semibold transition-colors w-full sm:w-auto"
                  data-testid="product-whatsapp-cta"
                >
                  WhatsApp ile İletişim
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
